import { LUCENT_CONFIG_PARAM, resolveLucentConfig, type LucentConfig } from "./config";
import { forwardRWorkerLog, isLucentDebug, lucentInfo } from "./debug";
import { RWASM } from "./rwasm-constants";
import { createServiceDriver, type ServiceDriver } from "./rWasmServiceDriver";
import { loadTransport, type HttpuvTransport } from "./transport";
import { connectHttpuvComlink } from "./wiring";

const RUN_WEB_APP_R = `
local({
  loadNamespace("shiny")
  shiny::startApp(appDir = "webApp", port = 3838L, host = "127.0.0.1", launch.browser = FALSE, quiet = TRUE)
})
`;

const config: LucentConfig = resolveLucentConfig();

let transport: HttpuvTransport | null = null;
let rWorker: Worker | null = null;
let rWorkerPromise: Promise<Worker> | null = null;
let comlinkConnected = false;
let comlinkPromise: Promise<void> | null = null;
let comlinkHandshakeInFlight = false;
let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let httpuvReadyPromise: Promise<void> | null = null;
let evalSeq = 0;
let serviceDriver: ServiceDriver | null = null;

const HTTPUV_SW_RELOAD_KEY = "httpuv-sw-reload";

interface RWorkerMessage {
  id?: string;
  type?: string;
  level?: string;
  text?: string;
  ok?: boolean;
  error?: string;
  message?: string;
  paths?: Record<string, string>;
  hadWork?: boolean;
  nextDelayMs?: number;
  delayMs?: number;
  percent?: number | null;
  downloadedBytes?: number;
  totalBytes?: number;
  phase?: string;
}

function requireTransport(): HttpuvTransport {
  if (!transport) {
    throw new Error("[lucent] transport not loaded yet");
  }
  return transport;
}

/**
 * Absolute base URL under which the Shiny app is mounted. Deliberately NOT
 * derived from import.meta.url (the bundle lives in /lucent/dist/, which would
 * collide with real static files); defaults to the origin root → `/shiny/`.
 */
function shinyBaseUrl(): string {
  return new URL(config.shinyBaseUrl, self.location.href).href;
}

function shinyPrefix(): string {
  return requireTransport().resolveShinyPrefix(shinyBaseUrl());
}

function appUrl(subpath = ""): string {
  return requireTransport().shinyAppUrl(subpath, shinyBaseUrl());
}

function serviceWorkerScriptUrl(): URL {
  const url = new URL(config.serviceWorkerUrl, self.location.href);
  // Tell the SW its mount prefix up-front so it intercepts correctly before the
  // REGISTER_HOST message arrives (avoids a first-load race on asset requests).
  url.searchParams.set("shinyPrefix", shinyPrefix());
  return url;
}

/** Max default scope for the SW script (its directory). Avoid forcing `/` so
 * project GitHub Pages mounts (`/repo/httpuv-sw.js` → scope `/repo/`) work
 * without `Service-Worker-Allowed`. */
function serviceWorkerScope(): string {
  return new URL("./", serviceWorkerScriptUrl()).pathname;
}

function announceHostToServiceWorker(): boolean {
  const t = requireTransport();
  const prefix = shinyPrefix();
  const msg = {
    type: t.MSG.REGISTER_HOST,
    shinyPrefix: prefix,
  };
  const controller = navigator.serviceWorker.controller;
  if (controller) {
    controller.postMessage(msg);
    lucentInfo("[httpuv] Announced host to service worker");
    return true;
  }
  return false;
}

async function waitForServiceWorkerController(
  timeoutMs = 3_000,
): Promise<ServiceWorker> {
  if (navigator.serviceWorker.controller) {
    return navigator.serviceWorker.controller;
  }

  await navigator.serviceWorker.ready;
  if (navigator.serviceWorker.controller) {
    return navigator.serviceWorker.controller;
  }

  return new Promise((resolve, reject) => {
    const deadline = setTimeout(() => {
      navigator.serviceWorker.removeEventListener("controllerchange", onController);
      reject(new Error("timeout"));
    }, timeoutMs);

    const onController = () => {
      if (navigator.serviceWorker.controller) {
        clearTimeout(deadline);
        navigator.serviceWorker.removeEventListener("controllerchange", onController);
        resolve(navigator.serviceWorker.controller);
      }
    };

    navigator.serviceWorker.addEventListener("controllerchange", onController);
    // Race: controller may appear between ready check and listener attach.
    onController();
  });
}

async function waitForServiceWorkerInstall(
  reg: ServiceWorkerRegistration,
  timeoutMs = 15_000,
): Promise<void> {
  const worker = reg.waiting ?? reg.installing ?? reg.active;
  if (!worker) {
    await navigator.serviceWorker.ready;
    return;
  }

  if (
    worker.state === "activated" ||
    worker.state === "installed"
  ) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const onStateChange = () => {
      if (
        worker.state === "activated" ||
        worker.state === "installed" ||
        worker.state === "redundant"
      ) {
        cleanup();
        resolve();
      }
    };

    const deadline = setTimeout(() => {
      cleanup();
      if (worker.state === "installed") {
        resolve();
        return;
      }
      reject(new Error(`Service worker activation timed out (state: ${worker.state})`));
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(deadline);
      worker.removeEventListener("statechange", onStateChange);
    };

    worker.addEventListener("statechange", onStateChange);
    onStateChange();
  });
}

function serviceWorkerScriptPath(): string {
  return serviceWorkerScriptUrl().href.split("?")[0];
}

function controllerMatchesExpectedScript(): boolean {
  const expected = serviceWorkerScriptPath();
  const current = navigator.serviceWorker.controller?.scriptURL.split("?")[0];
  return current === expected;
}

/** Drop httpuv-sw registrations from an older script URL. */
async function cleanupStaleHttpuvServiceWorkers(): Promise<void> {
  const expected = serviceWorkerScriptPath();
  for (const reg of await navigator.serviceWorker.getRegistrations()) {
    const scriptUrls = [reg.active, reg.waiting, reg.installing]
      .filter((worker): worker is ServiceWorker => worker != null)
      .map((worker) => worker.scriptURL.split("?")[0]);
    const isHttpuv = scriptUrls.some((url) => url.endsWith("/httpuv-sw.js"));
    if (!isHttpuv) {
      continue;
    }
    if (!scriptUrls.includes(expected)) {
      lucentInfo("[httpuv] Unregistering stale service worker", scriptUrls);
      await reg.unregister();
    }
  }
}

async function registerHttpuvServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    console.warn("[httpuv] Service workers are not supported in this browser");
    return null;
  }

  try {
    await cleanupStaleHttpuvServiceWorkers();

    const reg = await navigator.serviceWorker.register(serviceWorkerScriptUrl(), {
      scope: serviceWorkerScope(),
      updateViaCache: "none",
    });
    await waitForServiceWorkerInstall(reg);

    if (reg.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
      await waitForServiceWorkerInstall(reg, 5_000).catch(() => undefined);
    }

    if (!navigator.serviceWorker.controller || !controllerMatchesExpectedScript()) {
      const reloaded = sessionStorage.getItem(HTTPUV_SW_RELOAD_KEY);
      if (!reloaded) {
        sessionStorage.setItem(HTTPUV_SW_RELOAD_KEY, "1");
        lucentInfo("[httpuv] Service worker installed — reloading once to activate");
        window.location.reload();
        await new Promise(() => {});
      }
      console.warn(
        "[httpuv] Page still not controlled by the expected worker; check Application → Service Workers for httpuv-sw.js errors",
      );
    } else {
      sessionStorage.removeItem(HTTPUV_SW_RELOAD_KEY);
    }

    await waitForServiceWorkerController().catch(() => undefined);
    announceHostToServiceWorker();
    lucentInfo("[httpuv] Service worker registered", {
      scope: reg.scope,
      shinyPrefix: shinyPrefix(),
      controller: Boolean(navigator.serviceWorker.controller),
    });
    return reg;
  } catch (err) {
    console.error("[httpuv] Service worker registration failed:", err);
    throw err;
  }
}

function ensureHttpuvServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!swRegistrationPromise) {
    swRegistrationPromise = registerHttpuvServiceWorker();
  }
  return swRegistrationPromise;
}

function postToRWorker(
  worker: Worker,
  msg: Record<string, unknown>,
  transfer: Transferable[] = [],
): Promise<RWorkerMessage> {
  const id = (msg.id as string | undefined) ?? `m${++evalSeq}`;
  const payload = { ...msg, id };

  return new Promise((resolve, reject) => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as RWorkerMessage | undefined;
      if (!data || data.id !== id) {
        if (data?.type === RWASM.ERROR && !msg.id) {
          worker.removeEventListener("message", onMessage);
          reject(new Error(data.message ?? "R worker failed"));
        }
        return;
      }

      if (
        data.type === RWASM.EVAL_RESULT ||
        data.type === RWASM.STOPPED ||
        data.type === RWASM.RESOURCE_PATHS
      ) {
        worker.removeEventListener("message", onMessage);
        if (data.type === RWASM.STOPPED || data.type === RWASM.RESOURCE_PATHS || data.ok) {
          resolve(data);
        } else {
          reject(new Error(data.error ?? "eval failed"));
        }
      }
    };

    worker.addEventListener("message", onMessage);
    worker.postMessage(payload, transfer);
  });
}

/**
 * Serialize the host's resolved config into an absolute-URL payload for the
 * worker. Base URLs are made absolute against the host page so the worker (which
 * lives at a different path, /lucent/dist/) resolves them identically — the host
 * is the single source of truth for config.
 */
function workerConfigParam(): string {
  const base = self.location.href;
  const abs = (u?: string): string | undefined => (u == null ? undefined : new URL(u, base).href);
  const payload: Partial<LucentConfig> = {
    transportBaseUrl: abs(config.transportBaseUrl),
    serviceWorkerUrl: abs(config.serviceWorkerUrl),
    rRuntimeBaseUrl: abs(config.rRuntimeBaseUrl),
    empackMetaUrl: abs(config.empackMetaUrl),
    empackPackagesBaseUrl: abs(config.empackPackagesBaseUrl),
    shinyBaseUrl: abs(config.shinyBaseUrl),
  };
  return JSON.stringify(payload);
}

function dispatchLucentEvent(
  name: "lucent:download-progress" | "lucent:boot-status" | "lucent:ready" | "lucent:error",
  detail?: Record<string, unknown>,
): void {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function reportBootStatus(
  phase: string,
  message: string,
  percent: number | null,
): void {
  dispatchLucentEvent("lucent:boot-status", { phase, message, percent });
}

/** Forward worker progress/log side-channels for the whole worker lifetime. */
function installWorkerUiEvents(worker: Worker): void {
  worker.addEventListener("message", (event: MessageEvent) => {
    const data = event.data as RWorkerMessage | undefined;
    if (data?.type === RWASM.LOG) {
      forwardRWorkerLog(data.level === "error" ? "error" : "log", data.text ?? "");
      return;
    }
    if (data?.type === RWASM.DOWNLOAD_PROGRESS) {
      dispatchLucentEvent("lucent:download-progress", {
        percent: data.percent ?? null,
        downloadedBytes: data.downloadedBytes ?? 0,
        totalBytes: data.totalBytes ?? 0,
      });
      return;
    }
    if (data?.type === RWASM.BOOT_STATUS) {
      dispatchLucentEvent("lucent:boot-status", {
        phase: data.phase ?? "",
        message: data.message ?? "",
        percent: data.percent ?? null,
      });
    }
  });
}

function createRWorker(): Promise<Worker> {
  const workerUrl = new URL("./rWasmWorker.js", import.meta.url);
  workerUrl.searchParams.set(LUCENT_CONFIG_PARAM, workerConfigParam());
  if (requireTransport().isHttpuvDebug()) {
    workerUrl.searchParams.set("httpuvDebug", "1");
  }
  const worker = new Worker(workerUrl, { type: "module" });
  installWorkerUiEvents(worker);

  return new Promise((resolve, reject) => {
    const onBoot = (event: MessageEvent) => {
      const data = event.data as RWorkerMessage | undefined;
      if (
        data?.type === RWASM.LOG ||
        data?.type === RWASM.DOWNLOAD_PROGRESS ||
        data?.type === RWASM.BOOT_STATUS
      ) {
        // Handled by installWorkerUiEvents.
        return;
      }
      if (data?.type === RWASM.READY) {
        worker.removeEventListener("message", onBoot);
        lucentInfo("[runApp] R.wasm worker ready");
        resolve(worker);
        return;
      }
      if (data?.type === RWASM.ERROR) {
        worker.removeEventListener("message", onBoot);
        reject(new Error(data.message ?? "R worker bootstrap failed"));
      }
    };

    worker.addEventListener("message", onBoot);
    worker.addEventListener("error", (event) => {
      worker.removeEventListener("message", onBoot);
      const detail = [event.message, event.filename, event.lineno]
        .filter(Boolean)
        .join(" ");
      reject(new Error(detail ? `R worker failed to load: ${detail}` : "R worker failed to load"));
    });
  });
}

async function ensureRWorker(): Promise<Worker> {
  if (rWorker) {
    return rWorker;
  }
  if (!rWorkerPromise) {
    rWorkerPromise = createRWorker().then((worker) => {
      rWorker = worker;
      if (!serviceDriver) {
        serviceDriver = createServiceDriver({
          onHadWork: scheduleDebouncedResourcePathSync,
        });
      }
      serviceDriver.attach(worker);
      return worker;
    });
  }
  return rWorkerPromise;
}

/**
 * Establish the host <-> service worker <-> R worker Comlink link, coalescing
 * every caller onto a single handshake.
 *
 * Two handshakes in flight at once are destructive: each COMLINK_PORT resets the
 * worker's readiness flags, so the second handoff can clear the first one's
 * delivery registration, and the host cannot tell the two acks apart. A
 * controllerchange during boot used to start exactly such a competing handshake.
 */
function ensureComlinkConnected(): Promise<void> {
  if (comlinkPromise && (comlinkConnected || comlinkHandshakeInFlight)) {
    return comlinkPromise;
  }

  comlinkHandshakeInFlight = true;
  const handshake = (async () => {
    lucentInfo("[runApp] Waiting for R worker and service worker…");
    const [worker] = await Promise.all([ensureRWorker(), ensureHttpuvServiceWorker()]);
    if (!navigator.serviceWorker.controller || !controllerMatchesExpectedScript()) {
      throw new Error("Service worker controller is not available");
    }
    lucentInfo("[runApp] Connecting Comlink…");
    const { COMLINK } = requireTransport();
    await connectHttpuvComlink(worker, COMLINK.PORT_HANDOFF, {
      ackType: COMLINK.PORT_HANDOFF_ACK,
    });
    comlinkConnected = true;
  })();

  const clearInFlight = (): void => {
    comlinkHandshakeInFlight = false;
  };
  handshake.then(clearInFlight, clearInFlight);

  comlinkPromise = handshake;
  return handshake;
}

/**
 * Force the next ensureHttpuvReady() to redo the handshake. An in-flight
 * handshake is left in place as the coalescing target rather than raced.
 */
function invalidateComlink(): void {
  comlinkConnected = false;
  httpuvReadyPromise = null;
  if (!comlinkHandshakeInFlight) {
    comlinkPromise = null;
  }
}

function reconnectComlinkAfterServiceWorkerUpdate(): void {
  invalidateComlink();
  announceHostToServiceWorker();
  void ensureComlinkConnected().catch((err) => {
    console.warn("[httpuv] Comlink reconnect after service worker update failed:", err);
  });
}

export async function ensureHttpuvReady(): Promise<void> {
  const t = requireTransport();
  t.setShinyPrefix(shinyPrefix());
  // Do not treat a stale resolved promise as ready after SW reset/reconnect.
  if (comlinkConnected && comlinkPromise) {
    return comlinkPromise;
  }
  httpuvReadyPromise = ensureComlinkConnected();
  return httpuvReadyPromise;
}

function stopRunningApp(): void {
  navigator.serviceWorker.controller?.postMessage({ type: requireTransport().MSG.STOP });

  serviceDriver?.stop();

  if (rWorker) {
    rWorker.postMessage({ type: RWASM.STOP_APP });
  }
  lucentInfo("[runApp] App stopped");
}

function loadViewerFrame(): void {
  const frame = document.getElementById("app-frame") as HTMLIFrameElement | null;
  const url = appUrl();
  if (frame) {
    frame.src = url;
  }
  lucentInfo("[runApp] Viewer iframe →", url);
}

function clearAppDocumentCache(): void {
  lastResourcePaths = {};
  navigator.serviceWorker.controller?.postMessage({
    type: requireTransport().MSG.CLEAR_APP_CACHE,
  });
}

const RESOURCE_PATH_SYNC_DEBOUNCE_MS = 100;
let resourcePathSyncTimer: ReturnType<typeof setTimeout> | null = null;
let lastResourcePaths: Record<string, string> = {};
let resourcePathSyncInFlight = false;

function scheduleDebouncedResourcePathSync(): void {
  if (resourcePathSyncTimer) {
    clearTimeout(resourcePathSyncTimer);
  }
  resourcePathSyncTimer = setTimeout(() => {
    resourcePathSyncTimer = null;
    if (rWorker) {
      void syncResourcePathsIncremental(rWorker);
    }
  }, RESOURCE_PATH_SYNC_DEBOUNCE_MS);
}

function rememberResourcePaths(paths: Record<string, string>): void {
  lastResourcePaths = { ...paths };
}

async function syncResourcePathsToServiceWorker(worker: Worker): Promise<void> {
  const controller = navigator.serviceWorker.controller;
  if (!controller) {
    return;
  }

  try {
    const data = await postToRWorker(worker, { type: RWASM.GET_RESOURCE_PATHS });
    const paths = data.paths ?? {};
    rememberResourcePaths(paths);
    controller.postMessage({ type: requireTransport().MSG.REGISTER_RESOURCE_PATHS, paths });
    if (Object.keys(paths).length > 0) {
      lucentInfo("[runApp] synced", Object.keys(paths).length, "resource path(s) to SW");
    }
  } catch (err) {
    console.warn("[runApp] resource path sync failed; SW will fall through to R", err);
  }
}

/** Diff resourcePaths() against the last SW snapshot and send incremental updates. */
async function syncResourcePathsIncremental(worker: Worker): Promise<void> {
  if (resourcePathSyncInFlight) {
    scheduleDebouncedResourcePathSync();
    return;
  }
  const controller = navigator.serviceWorker.controller;
  if (!controller) {
    return;
  }

  resourcePathSyncInFlight = true;
  try {
    const data = await postToRWorker(worker, { type: RWASM.GET_RESOURCE_PATHS });
    const paths = data.paths ?? {};
    const updateType = requireTransport().MSG.UPDATE_RESOURCE_PATH;
    let changed = 0;
    for (const prefix of Object.keys(paths)) {
      if (lastResourcePaths[prefix] !== paths[prefix]) {
        controller.postMessage({ type: updateType, prefix, dir: paths[prefix] });
        changed += 1;
      }
    }
    for (const prefix of Object.keys(lastResourcePaths)) {
      if (!(prefix in paths)) {
        controller.postMessage({ type: updateType, prefix, dir: "" });
        changed += 1;
      }
    }
    rememberResourcePaths(paths);
    if (changed > 0) {
      lucentInfo("[runApp] updated", changed, "resource path(s) on SW");
    }
  } catch (err) {
    console.warn("[runApp] incremental resource path sync failed", err);
  } finally {
    resourcePathSyncInFlight = false;
  }
}

async function waitForShinyHttpReady(worker: Worker): Promise<void> {
  const t = requireTransport();
  const url = appUrl();
  reportBootStatus("warmup", "Warming up Shiny…", 92);
  lucentInfo("[runApp] Warming up Shiny (may take a minute on first load)…", url);
  const res = await fetch(url, {
    cache: "no-store",
    headers: { [t.WARMUP_REQUEST_HEADER]: "1" },
    signal: AbortSignal.timeout(t.REQUEST_TIMEOUT_MS),
  });
  if (!res.ok) {
    throw new Error(`Shiny warmup GET ${url} failed: HTTP ${res.status}`);
  }
  lucentInfo("[runApp] Shiny warmup OK (HTTP", res.status + ")");
  await syncResourcePathsToServiceWorker(worker);
}

/**
 * Start (or restart) the Shiny app already present in the VFS at `/webApp`
 * (populated via empack pack dir + append during R bootstrap).
 */
export async function runApp(): Promise<number> {
  const worker = await ensureRWorker();

  clearAppDocumentCache();
  await postToRWorker(worker, { type: RWASM.STOP_APP });

  // Remount only when forced (?remountRHome=1) or when the worker detects that
  // the empack / runtime URLs changed since the last populate.
  const forceRemount =
    new URLSearchParams(self.location.search).get("remountRHome") === "1";
  await postToRWorker(worker, { type: RWASM.REMOUNT_R_HOME, force: forceRemount });

  lucentInfo("[runApp] worker eval", RUN_WEB_APP_R);
  reportBootStatus("start-app", "Starting Shiny…", 85);
  await postToRWorker(worker, {
    type: RWASM.EVAL,
    code: RUN_WEB_APP_R,
  });

  // Host rAF owns service ticks after startApp; wake once so init/flush can run.
  serviceDriver?.attach(worker);
  serviceDriver?.wake();

  return 0;
}

async function startShinyApp(): Promise<void> {
  await ensureHttpuvReady();
  const worker = await ensureRWorker();
  await runApp();
  // Remount / SW activate can drop the worker link after the first handshake.
  // Re-assert Comlink before warmup so GET /shiny/ does not race PORT_HANDOFF.
  invalidateComlink();
  await ensureHttpuvReady();
  await waitForShinyHttpReady(worker);
  loadViewerFrame();
  dispatchLucentEvent("lucent:ready");
}

function installHostServiceWorkerListeners(): void {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    reconnectComlinkAfterServiceWorkerUpdate();
  });

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === requireTransport().MSG.REQUEST_COMLINK) {
      reconnectComlinkAfterServiceWorkerUpdate();
    }
  });

  // Chromium stops the SW while idle and forgets hostClientId. Re-announce the
  // Lucent shell (not the /shiny/ iframe) whenever we become visible again.
  const reassertHost = () => {
    announceHostToServiceWorker();
    if (!comlinkConnected) {
      reconnectComlinkAfterServiceWorkerUpdate();
    }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      reassertHost();
    }
  });
  window.addEventListener("pageshow", reassertHost);
  window.addEventListener("focus", () => {
    announceHostToServiceWorker();
  });
}

function installGlobalHelpers(): void {
  const lucent = {
    shinyUrl: (subpath = "") => appUrl(subpath),
    ensureHttpuvReady,
    stopRunningApp,
    enableHttpuvDebug: () => requireTransport().enableHttpuvDebug(),
    /** Quick check that the worker responds. */
    ping: () => lucent.evalR("1+1"),
    async evalR(code: string): Promise<void> {
      const worker = await ensureRWorker();
      await postToRWorker(worker, {
        type: RWASM.EVAL,
        code,
      });
    },
    startShinyApp: () => startShinyApp(),
    async testVirtualSocket(message = '{"method":"ping"}') {
      await ensureHttpuvReady();
      const controller = navigator.serviceWorker.controller;
      if (!controller) {
        console.warn(
          "[lucent] No service worker controller — fetch may not be intercepted; unregister old workers and hard-refresh",
        );
        throw new Error("no service worker controller");
      }

      const openUrl = new URL("__session__/open", appUrl());
      lucentInfo("[lucent] testVirtualSocket: open", openUrl.href);
      const openRes = await fetch(openUrl, { method: "POST" });
      if (!openRes.ok) {
        throw new Error(`session open failed: HTTP ${openRes.status} ${await openRes.text()}`);
      }
      const { handle } = (await openRes.json()) as { handle: string };
      lucentInfo("[lucent] testVirtualSocket: handle", handle);

      const channel = new MessageChannel();
      const ack = new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("session ACK timed out")), 5_000);
        channel.port1.onmessage = (event: MessageEvent<{ type?: string }>) => {
          if (event.data?.type === requireTransport().MSG.SESSION_ACK) {
            clearTimeout(timer);
            resolve();
          }
        };
      });
      controller.postMessage(
        { type: requireTransport().MSG.REGISTER_SESSION, handle },
        [channel.port2],
      );
      await ack;
      lucentInfo("[lucent] testVirtualSocket: session port registered");

      const sendUrl = new URL(`__session__/send?handle=${encodeURIComponent(handle)}`, appUrl());
      const sendRes = await fetch(sendUrl, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: message,
      });
      if (!sendRes.ok && sendRes.status !== 204) {
        throw new Error(`session send failed: HTTP ${sendRes.status}`);
      }

      channel.port1.close();
      const result = { handle, registered: true, sendStatus: sendRes.status };
      lucentInfo("[lucent] testVirtualSocket: result", result);
      return result;
    },
  };
  (globalThis as unknown as { __lucent?: typeof lucent }).__lucent = lucent;
}

async function main(): Promise<void> {
  transport = await loadTransport(config.transportBaseUrl);
  transport.setShinyPrefix(shinyPrefix());

  installHostServiceWorkerListeners();
  installGlobalHelpers();

  if (isLucentDebug()) {
    lucentInfo("[runApp] httpuv debug tracing enabled (?httpuvDebug=1)");
  }

  void ensureHttpuvServiceWorker().catch((err) => {
    console.error("[httpuv] Service worker setup failed:", err);
  });

  await startShinyApp();
}

void main().catch((err) => {
  console.error("[runApp] Failed to start:", err);
  dispatchLucentEvent("lucent:error", {
    message: err instanceof Error ? err.message : String(err),
  });
});
